import React, { useState, useEffect } from 'react';
import {
  FiRefreshCw, FiCheckCircle, FiXCircle, FiClock, FiFilter, FiEye, FiAlertCircle, FiX, FiPlus, FiMessageSquare
} from 'react-icons/fi';
import {
  supportActionApi,
  SupportActionRequest,
  CreateActionRequest,
  TicketListItem,
  UserListItem,
} from '../services/supportApi';
import './SupportActionRequestManagement.css';

const Icon = ({ icon: IconComponent, className }: { icon: any; className?: string }) =>
  React.createElement(IconComponent as any, { className });

const SupportActionRequestManagement: React.FC = () => {
  const [requests, setRequests] = useState<SupportActionRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState<SupportActionRequest | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  
  // Create form state
  const [createForm, setCreateForm] = useState<CreateActionRequest>({
    ticketId: 0,
    targetUserId: 0,
    actionType: 'REFUND',
    reason: '',
    additionalData: {},
  });

  // For dropdowns
  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [filter, actionTypeFilter, page]);

  useEffect(() => {
    if (showCreateModal) {
      loadTicketsAndUsers();
    }
  }, [showCreateModal]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const params: any = {
        page,
        size,
      };

      if (filter !== 'all') {
        params.status = filter;
      }

      if (actionTypeFilter !== 'all') {
        params.actionType = actionTypeFilter;
      }

      const response = await supportActionApi.getAll(params);
      setRequests(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      console.error('Failed to load action requests:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.ticketId || !createForm.targetUserId || !createForm.reason.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await supportActionApi.request(createForm);
      setShowCreateModal(false);
      resetCreateForm();
      loadRequests();
      alert('Action request created successfully!');
    } catch (error: any) {
      console.error('Failed to create request:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const loadTicketsAndUsers = async () => {
    try {
      setLoadingTickets(true);
      setLoadingUsers(true);
      const [ticketsData, usersData] = await Promise.all([
        supportActionApi.getTicketsList(),
        supportActionApi.getUsersList(),
      ]);
      setTickets(ticketsData);
      setUsers(usersData);
    } catch (error: any) {
      console.error('Failed to load tickets/users:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoadingTickets(false);
      setLoadingUsers(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      ticketId: 0,
      targetUserId: 0,
      actionType: 'REFUND',
      reason: '',
      additionalData: {},
    });
  };

  const openViewModal = async (request: SupportActionRequest) => {
    try {
      // Load full request details
      const fullRequest = await supportActionApi.getById(request.id);
      setSelectedRequest(fullRequest);
      setShowViewModal(true);
    } catch (error: any) {
      console.error('Failed to load request details:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return '#ff9800';
      case 'APPROVED': return '#4caf50';
      case 'REJECTED': return '#f44336';
      case 'COMPLETED': return '#2196f3';
      case 'FAILED': return '#9e9e9e';
      default: return '#666';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING': return FiClock;
      case 'APPROVED': return FiCheckCircle;
      case 'REJECTED': return FiXCircle;
      case 'COMPLETED': return FiCheckCircle;
      case 'FAILED': return FiAlertCircle;
      default: return FiClock;
    }
  };

  const formatActionType = (actionType: string) => {
    // Handle special cases for better formatting
    const actionTypeMap: Record<string, string> = {
      'REFUND': 'Refund',
      'FREEZE_ACCOUNT': 'Freeze Account',
      'UNFREEZE_ACCOUNT': 'Unfreeze Account',
      'DELETE_ACCOUNT': 'Delete Account',
      'RESET_PASSWORD': 'Reset Password',
      'ADJUST_SUBSCRIPTION': 'Adjust Subscription',
      'EXTEND_SUBSCRIPTION': 'Extend Subscription',
      'CANCEL_SUBSCRIPTION': 'Cancel Subscription',
      'WAIVE_FEE': 'Waive Fee',
      'APPLY_CREDIT': 'Apply Credit',
      'OTHER': 'Other',
    };

    if (actionTypeMap[actionType]) {
      return actionTypeMap[actionType];
    }

    // Fallback: format underscore-separated words
    return actionType
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getActionTypeColor = (actionType: string) => {
    // Color coding for different action types
    const colorMap: Record<string, string> = {
      'REFUND': '#f44336', // Red - financial action
      'FREEZE_ACCOUNT': '#ff9800', // Orange - account restriction
      'UNFREEZE_ACCOUNT': '#4caf50', // Green - account restoration
      'DELETE_ACCOUNT': '#dc3545', // Red - destructive action
      'RESET_PASSWORD': '#2196f3', // Blue - account support
      'ADJUST_SUBSCRIPTION': '#00bcd4', // Cyan - subscription change
      'EXTEND_SUBSCRIPTION': '#4caf50', // Green - positive action
      'CANCEL_SUBSCRIPTION': '#f44336', // Red - cancellation
      'WAIVE_FEE': '#ff9800', // Orange - financial adjustment
      'APPLY_CREDIT': '#4caf50', // Green - positive financial action
      'OTHER': '#9e9e9e', // Grey - other
    };

    return colorMap[actionType] || '#666';
  };

  return (
    <div className="support-action-request-management">
      <div className="sarm-header">
        <h2>
          <Icon icon={FiCheckCircle} /> Support Action Requests
        </h2>
        <div className="sarm-header-actions">
          <button
            className="btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <Icon icon={FiPlus} /> Create Request
          </button>
          <button
            className="btn-secondary"
            onClick={loadRequests}
            disabled={loading}
          >
            <Icon icon={FiRefreshCw} /> Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="sarm-filters">
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setPage(0); }}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="COMPLETED">Completed</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Action Type:</label>
          <select
            value={actionTypeFilter}
            onChange={(e) => { setActionTypeFilter(e.target.value); setPage(0); }}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="REFUND">Refund</option>
            <option value="FREEZE_ACCOUNT">Freeze Account</option>
            <option value="UNFREEZE_ACCOUNT">Unfreeze Account</option>
            <option value="DELETE_ACCOUNT">Delete Account</option>
            <option value="RESET_PASSWORD">Reset Password</option>
            <option value="ADJUST_SUBSCRIPTION">Adjust Subscription</option>
            <option value="EXTEND_SUBSCRIPTION">Extend Subscription</option>
            <option value="CANCEL_SUBSCRIPTION">Cancel Subscription</option>
            <option value="WAIVE_FEE">Waive Fee</option>
            <option value="APPLY_CREDIT">Apply Credit</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="sarm-summary">
        <div className="summary-item">
          <span className="summary-label">Total Requests:</span>
          <span className="summary-value">{totalElements}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Pending:</span>
          <span className="summary-value warning">
            {requests.filter(r => r.status === 'PENDING').length}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">My Requests:</span>
          <span className="summary-value info">
            {requests.filter(r => r.requestedBy?.id).length}
          </span>
        </div>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="sarm-loading">
          <div className="spinner"></div>
          <p>Loading action requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="sarm-empty">
          <Icon icon={FiCheckCircle} />
          <p>No action requests found</p>
        </div>
      ) : (
        <>
          <div className="sarm-requests-list">
            {requests.map((request) => (
              <div key={request.id} className="sarm-request-card">
                <div className="sarm-request-header">
                  <div className="sarm-request-id">
                    <strong>Request #{request.id}</strong>
                    <span 
                      className="action-type-badge"
                      style={{ 
                        backgroundColor: getActionTypeColor(request.actionType),
                        color: 'white'
                      }}
                    >
                      {formatActionType(request.actionType)}
                    </span>
                  </div>
                  <div
                    className="sarm-status-badge"
                    style={{ backgroundColor: getStatusColor(request.status) }}
                  >
                    <Icon icon={getStatusIcon(request.status)} />
                    {request.status}
                  </div>
                </div>

                <div className="sarm-request-content">
                  <div className="sarm-request-info">
                    <div className="info-row">
                      <strong>Ticket ID:</strong> #{request.ticketId}
                    </div>
                    <div className="info-row">
                      <strong>Target User:</strong> {request.targetUser?.name || `User #${request.targetUserId}`}
                      {request.targetUser?.phoneNumber && ` (${request.targetUser.phoneNumber})`}
                    </div>
                    <div className="info-row">
                      <strong>Reason:</strong> {request.reason}
                    </div>
                    {request.requestedBy && (
                      <div className="info-row">
                        <strong>Requested By:</strong> {request.requestedBy.name}
                      </div>
                    )}
                    {request.approvedBy && (
                      <div className="info-row">
                        <strong>Approved By:</strong> User #{request.approvedBy}
                        {request.approvedAt && ` at ${new Date(request.approvedAt).toLocaleString()}`}
                      </div>
                    )}
                    {request.rejectedBy && (
                      <div className="info-row">
                        <strong>Rejected By:</strong> User #{request.rejectedBy}
                        {request.rejectedAt && ` at ${new Date(request.rejectedAt).toLocaleString()}`}
                        {request.rejectionReason && (
                          <div className="rejection-reason">
                            <strong>Reason:</strong> {request.rejectionReason}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="info-row">
                      <strong>Created:</strong> {new Date(request.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="sarm-request-actions">
                  <button
                    className="btn-view"
                    onClick={() => openViewModal(request)}
                  >
                    <Icon icon={FiEye} /> View Details
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="sarm-pagination">
              <button
                className="btn-pagination"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {page + 1} of {totalPages} ({totalElements} total)
              </span>
              <button
                className="btn-pagination"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Create Request Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => { setShowCreateModal(false); resetCreateForm(); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Action Request</h3>
              <button className="btn-close" onClick={() => { setShowCreateModal(false); resetCreateForm(); }}>
                <Icon icon={FiX} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Select Ticket *</label>
                {loadingTickets ? (
                  <div className="loading-indicator">Loading tickets...</div>
                ) : (
                  <select
                    value={createForm.ticketId || ''}
                    onChange={(e) => {
                      const ticketId = parseInt(e.target.value);
                      const selectedTicket = tickets.find(t => t.id === ticketId);
                      setCreateForm({
                        ...createForm,
                        ticketId: ticketId || 0,
                        // Auto-fill targetUserId from ticket when ticket is selected
                        targetUserId: selectedTicket ? selectedTicket.userId : createForm.targetUserId,
                      });
                    }}
                    className="form-select"
                    required
                  >
                    <option value="">-- Select a Ticket --</option>
                    {tickets.map(ticket => (
                      <option key={ticket.id} value={ticket.id}>
                        #{ticket.id} - {ticket.subject} - {ticket.userName} ({ticket.userPhoneNumber}) - {ticket.status}
                      </option>
                    ))}
                  </select>
                )}
                {createForm.ticketId > 0 && (
                  <div className="ticket-info-preview">
                    {(() => {
                      const selectedTicket = tickets.find(t => t.id === createForm.ticketId);
                      return selectedTicket ? (
                        <>
                          <strong>Selected Ticket:</strong> #{selectedTicket.id} - {selectedTicket.subject}
                          <br />
                          <strong>Ticket User:</strong> {selectedTicket.userName} ({selectedTicket.userPhoneNumber})
                          <br />
                          <strong>Status:</strong> {selectedTicket.status}
                        </>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Target User *</label>
                {loadingUsers ? (
                  <div className="loading-indicator">Loading users...</div>
                ) : (
                  <select
                    value={createForm.targetUserId || ''}
                    onChange={(e) => setCreateForm({ ...createForm, targetUserId: parseInt(e.target.value) || 0 })}
                    className="form-select"
                    required
                  >
                    <option value="">-- Select a User --</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        #{user.id} - {user.name} ({user.phoneNumber}) - {user.email} - {user.role}
                      </option>
                    ))}
                  </select>
                )}
                {createForm.targetUserId > 0 && (
                  <div className="user-info-preview">
                    {(() => {
                      const selectedUser = users.find(u => u.id === createForm.targetUserId);
                      return selectedUser ? (
                        <>
                          <strong>Selected User:</strong> #{selectedUser.id} - {selectedUser.name}
                          <br />
                          <strong>Phone:</strong> {selectedUser.phoneNumber}
                          <br />
                          <strong>Email:</strong> {selectedUser.email}
                          <br />
                          <strong>Role:</strong> {selectedUser.role}
                        </>
                      ) : null;
                    })()}
                  </div>
                )}
                {createForm.ticketId > 0 && !createForm.targetUserId && (
                  <div className="info-hint">
                    <small>
                      ⓘ Tip: The user from the selected ticket will be auto-filled, but you can select a different user if needed.
                    </small>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Action Type *</label>
                <select
                  value={createForm.actionType}
                  onChange={(e) => setCreateForm({ ...createForm, actionType: e.target.value })}
                  className="form-select"
                >
                  <option value="REFUND">Refund</option>
                  <option value="FREEZE_ACCOUNT">Freeze Account</option>
                  <option value="UNFREEZE_ACCOUNT">Unfreeze Account</option>
                  <option value="DELETE_ACCOUNT">Delete Account</option>
                  <option value="RESET_PASSWORD">Reset Password</option>
                  <option value="ADJUST_SUBSCRIPTION">Adjust Subscription</option>
                  <option value="EXTEND_SUBSCRIPTION">Extend Subscription</option>
                  <option value="CANCEL_SUBSCRIPTION">Cancel Subscription</option>
                  <option value="WAIVE_FEE">Waive Fee</option>
                  <option value="APPLY_CREDIT">Apply Credit</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Reason *</label>
                <textarea
                  value={createForm.reason}
                  onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })}
                  placeholder="Explain why this action is needed..."
                  rows={5}
                  required
                />
              </div>
              <div className="form-group">
                <label>Additional Data (JSON - Optional)</label>
                <textarea
                  value={JSON.stringify(createForm.additionalData || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setCreateForm({ ...createForm, additionalData: parsed });
                    } catch {
                      // Invalid JSON, ignore
                    }
                  }}
                  placeholder='{"amount": 100, "currency": "SAR"}'
                  rows={4}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Optional: Add additional data as JSON (e.g., amount, transaction ID)
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowCreateModal(false); resetCreateForm(); }}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleCreate}
                disabled={!createForm.ticketId || !createForm.targetUserId || !createForm.reason.trim() || loadingTickets || loadingUsers}
              >
                <Icon icon={FiPlus} /> Create Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => { setShowViewModal(false); setSelectedRequest(null); }}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Action Request Details</h3>
              <button className="btn-close" onClick={() => { setShowViewModal(false); setSelectedRequest(null); }}>
                <Icon icon={FiX} />
              </button>
            </div>
            <div className="modal-body">
              <div className="request-details-view">
                <div className="detail-section">
                  <h4>Request Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <strong>Request ID:</strong> #{selectedRequest.id}
                    </div>
                    <div className="detail-item">
                      <strong>Status:</strong>
                      <span 
                        className="status-badge-inline"
                        style={{ backgroundColor: getStatusColor(selectedRequest.status) }}
                      >
                        {selectedRequest.status}
                      </span>
                    </div>
                    <div className="detail-item">
                      <strong>Action Type:</strong>
                      <span 
                        className="action-type-badge-inline"
                        style={{ backgroundColor: getActionTypeColor(selectedRequest.actionType) }}
                      >
                        {formatActionType(selectedRequest.actionType)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <strong>Ticket ID:</strong> #{selectedRequest.ticketId}
                    </div>
                    <div className="detail-item">
                      <strong>Target User:</strong> {selectedRequest.targetUser?.name || `User #${selectedRequest.targetUserId}`}
                      {selectedRequest.targetUser?.phoneNumber && ` (${selectedRequest.targetUser.phoneNumber})`}
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Reason</h4>
                  <div className="reason-box">
                    {selectedRequest.reason}
                  </div>
                </div>

                {selectedRequest.additionalData && Object.keys(selectedRequest.additionalData).length > 0 && (
                  <div className="detail-section">
                    <h4>Additional Data</h4>
                    <pre className="additional-data-view">
                      {JSON.stringify(selectedRequest.additionalData, null, 2)}
                    </pre>
                  </div>
                )}

                <div className="detail-section">
                  <h4>Request History</h4>
                  <div className="history-timeline">
                    <div className="history-item">
                      <strong>Created:</strong> {new Date(selectedRequest.createdAt).toLocaleString()}
                      {selectedRequest.requestedBy && (
                        <span> by {selectedRequest.requestedBy.name}</span>
                      )}
                    </div>
                    {selectedRequest.approvedAt && (
                      <div className="history-item approved">
                        <strong>Approved:</strong> {new Date(selectedRequest.approvedAt).toLocaleString()}
                        {selectedRequest.approvedBy && (
                          <span> by User #{selectedRequest.approvedBy}</span>
                        )}
                      </div>
                    )}
                    {selectedRequest.rejectedAt && (
                      <div className="history-item rejected">
                        <strong>Rejected:</strong> {new Date(selectedRequest.rejectedAt).toLocaleString()}
                        {selectedRequest.rejectedBy && (
                          <span> by User #{selectedRequest.rejectedBy}</span>
                        )}
                        {selectedRequest.rejectionReason && (
                          <div className="rejection-reason-box">
                            <strong>Reason:</strong> {selectedRequest.rejectionReason}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowViewModal(false); setSelectedRequest(null); }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportActionRequestManagement;
